import { action, computed, makeAutoObservable } from 'mobx';
import { GENDER_OTHERS, LIST_OF_COUNTRY, NATIONALITY_OPTIONS, TITLE } from 'src/constants';
import { ICountry, IOptions, IProvince } from 'src/interfaces/application';

export interface IApplicationStore {
	currentCountryCode: string;
	countries: ICountry[];
	provinces: IProvince[];
	provincesByCountry: IProvince[];
	titleOptions: IOptions[];
	genderOptions: IOptions[];
	genderAndXOptions: IOptions[];
	maritalStatuses: IOptions[];
	phoneCountryCode: IOptions[];
	placeOfBirthAndNationalities: IOptions[];
	nationalities: IOptions[];
	listOfCountry: IOptions[];

	filterProvinceByCode(code: string): void;
	get countriesOptions(): IOptions[];
	getCountryNameByCode(code?: string): string | undefined;

	getNationalityNameByCode(code?: string): string | undefined;

	getPersonTitleText(code: string | undefined): string;

	filterGenderByTitle(isChineseNationality: boolean, title: string): Array<IOptions>
}

const mockCountry: ICountry[] = [
	{
		id: 'AU',
		name: 'Australia',
		isoCode: 'AU',
		isCode3: 'AUS'
	},
	{
		id: 'CN',
		name: 'China',
		isoCode: 'CN',
		isCode3: 'CHN'
	},
	{
		id: 'CA',
		name: 'Canada',
		isoCode: 'CA',
		isCode3: 'CAN'
	},
	{
		id: 'HK',
		name: 'Hong Kong, China',
		isoCode: 'HK',
		isCode3: 'HKG'
	},
	{
		id: 'KR',
		name: 'South Korea',
		isoCode: 'KR',
		isCode3: 'KOR'
	},
	{
		id: 'PH',
		name: 'Philippine',
		isoCode: 'PH',
		isCode3: 'PHL'
	},
	{
		id: 'TW',
		name: 'Taiwan',
		isoCode: 'TW',
		isCode3: 'TWN'
	},
	{
		id: 'JP',
		name: 'Japan',
		isoCode: 'JP',
		isCode3: 'JPN'
	},
	{
		id: 'GB',
		name: 'United Kingdom',
		isoCode: 'GB',
		isCode3: 'GBR'
	},
	{
		id: 'OthersB',
		name: 'Others',
		isoCode: 'Others',
		isCode3: 'Others'
	}
];

const mockProvinces: IProvince[] = [
	// Hong Kong
	{
		id: 'HKP',
		name: 'Hong Kong',
		countryCode: 'HK'
	},
	// Shanghai
	{
		id: 'Shanghai',
		name: 'Shanghai',
		countryCode: 'CN'
	},
	{
		id: 'Others',
		name: 'Others',
		countryCode: 'CN'
	},
	// Taiwan
	{
		id: 'Taipei',
		name: 'Taipei',
		countryCode: 'TW'
	},
	{
		id: 'Others',
		name: 'Others',
		countryCode: 'TW'
	},
	// Japan
	{
		id: 'Tokyo',
		name: 'Tokyo',
		countryCode: 'JP'
	},
	{
		id: 'Osaka',
		name: 'Ōsaka',
		countryCode: 'JP'
	},
	{
		id: 'Fukuoka',
		name: 'Fukuoka',
		countryCode: 'JP'
	},
	{
		id: 'Kyoto',
		name: 'Kyoto',
		countryCode: 'JP'
	},
	{
		id: 'Others',
		name: 'Others',
		countryCode: 'JP'
	},
	// Australia
	{
		id: 'Sydney',
		name: 'Sydney',
		countryCode: 'AU'
	},
	{
		id: 'Melbourne',
		name: 'Melbourne',
		countryCode: 'AU'
	},
	{
		id: 'Brisbane',
		name: 'Brisbane',
		countryCode: 'AU'
	},
	{
		id: 'Gold coast',
		name: 'Gold coast',
		countryCode: 'AU'
	},
	{
		id: 'Others',
		name: 'Others',
		countryCode: 'AU'
	},
	// Canada
	{
		id: 'Vancouver',
		name: 'Vancouver',
		countryCode: 'CA'
	},
	{
		id: 'Toronto',
		name: 'Toronto',
		countryCode: 'CA'
	},
	{
		id: 'Calgary',
		name: 'Calgary',
		countryCode: 'CA'
	},
	{
		id: 'Others',
		name: 'Others',
		countryCode: 'CA'
	},
	// South Korea
	{
		id: 'Seoul',
		name: 'Seoul',
		countryCode: 'KR'
	},
	{
		id: 'Others',
		name: 'Others',
		countryCode: 'KR'
	},
	// United Kingdom
	{
		id: 'Birmingham',
		name: 'Birmingham',
		countryCode: 'GB'
	},
	{
		id: 'Manchester',
		name: 'Manchester',
		countryCode: 'GB'
	},
	{
		id: 'London',
		name: 'London',
		countryCode: 'GB'
	},
	{
		id: 'Others',
		name: 'Others',
		countryCode: 'GB'
	},
	// Philippine
	{
		id: 'Manila',
		name: 'Manila',
		countryCode: 'PH'
	},
	{
		id: 'Others',
		name: 'Others',
		countryCode: 'PH'
	},

	{
		id: 'Others',
		name: 'Others',
		countryCode: 'Others'
	}
];

export class ApplicationStore implements IApplicationStore {
	currentCountryCode = 'HK';
	countries: ICountry[] = mockCountry;
	provincesByCountry: IProvince[] = [];
	provinces: IProvince[] = mockProvinces;

	titleOptions: IOptions[] = [
		{
			id: 'ms',
			value: 'ms',
			key: 'ms',
			label: 'Ms.'
		},
		{
			id: 'mss',
			value: 'miss',
			key: 'miss',
			label: 'Miss'
		},
		{
			id: 'mrs',
			value: 'mrs',
			key: 'mrs',
			label: 'Mrs.'
		},
		{
			id: 'mr',
			value: 'mr',
			key: 'mr',
			label: 'Mr.'
		},
		{
			id: 'others',
			value: 'others',
			key: 'others',
			label: 'Others'
		}
	];
	genderOptions: IOptions[] = [
		{
			id: 'F',
			key: 'Female',
			value: 'F',
			label: 'Female'
		},
		{
			id: 'M',
			key: 'Male',
			value: 'M',
			label: 'Male'
		}
	];
	genderAndXOptions: IOptions[] = [
		{
			id: 'F',
			key: 'Female',
			value: 'F',
			label: 'Female'
		},
		{
			id: 'M',
			key: 'Male',
			value: 'M',
			label: 'Male'
		},
		{
			id: 'X',
			key: 'X',
			value: 'X',
			label: 'X'
		}
	]
	placeOfBirthAndNationalities: IOptions[] = [
		{
			id: 'australia',
			label: 'Australia',
			key: 'AU',
			value: 'AU'
		},
		{
			id: 'china',
			label: 'China',
			key: 'CN',
			value: 'CN'
		},
		{
			id: 'canada',
			label: 'Canada',
			key: 'CA',
			value: 'CA'
		},
		{
			id: 'hongkong',
			label: 'Hong Kong',
			key: 'HK',
			value: 'HK'
		},
		{
			id: 'southkorea',
			label: 'South Korea',
			key: 'KR',
			value: 'KR'
		},
		{
			id: 'philippine',
			label: 'Philippine',
			key: 'PH',
			value: 'PH'
		},
		{
			id: 'taiwan',
			label: 'Taiwan',
			key: 'TW',
			value: 'TW'
		},
		{
			id: 'japan',
			label: 'Japan',
			key: 'JP',
			value: 'JP'
		},
		{
			id: 'GB',
			label: 'United Kingdom',
			key: 'GB',
			value: 'GB'
		},
		{
			id: 'others',
			label: 'Others',
			key: 'others',
			value: 'others'
		}
	];

	nationalities: IOptions[] = NATIONALITY_OPTIONS.sort((a, b) => a.label.localeCompare(b.label));

	listOfCountry: IOptions[] = LIST_OF_COUNTRY.split(/\n/).map((item) => {
		const ios2CountryCode = item.trim().substring(0, item.indexOf(' '));
		const countryName = item
			.trim()
			.substring(item.indexOf(' ') + 1)
			.trim();

		return {
			id: ios2CountryCode,
			label: countryName,
			key: ios2CountryCode,
			value: ios2CountryCode
		};
	});

	maritalStatuses: IOptions[] = [
		{
			id: 'single',
			key: 'single',
			value: 'single',
			label: 'Single'
		},
		{
			id: 'married',
			key: 'married',
			value: 'married',
			label: 'Married'
		},
		{
			id: 'divorced',
			key: 'divorced',
			value: 'divorced',
			label: 'Divorced'
		},
		{
			id: 'other',
			key: 'other',
			value: 'other',
			label: 'Others'
		}
	];

	phoneCountryCode: IOptions[] = [
		{
			label: '+(93)',
			value: '93',
			id: '0',
			key: 'key0'
		},
		{
			label: '+(358)',
			value: '358',
			id: '1',
			key: 'key1'
		},
		{
			label: '+(355)',
			value: '355',
			id: '2',
			key: 'key2'
		},
		{
			label: '+(213)',
			value: '213',
			id: '3',
			key: 'key3'
		},
		{
			label: '+(1684)',
			value: '1684',
			id: '4',
			key: 'key4'
		},
		{
			label: '+(376)',
			value: '376',
			id: '5',
			key: 'key5'
		},
		{
			label: '+(244)',
			value: '244',
			id: '6',
			key: 'key6'
		},
		{
			label: '+(1264)',
			value: '1264',
			id: '7',
			key: 'key7'
		},
		{
			label: '+(672)',
			value: '672',
			id: '8',
			key: 'key8'
		},
		{
			label: '+(1268)',
			value: '1268',
			id: '9',
			key: 'key9'
		},
		{
			label: '+(54)',
			value: '54',
			id: '10',
			key: 'key10'
		},
		{
			label: '+(374)',
			value: '374',
			id: '11',
			key: 'key11'
		},
		{
			label: '+(297)',
			value: '297',
			id: '12',
			key: 'key12'
		},
		{
			label: '+(61)',
			value: '61',
			id: '13',
			key: 'key13'
		},
		{
			label: '+(43)',
			value: '43',
			id: '14',
			key: 'key14'
		},
		{
			label: '+(994)',
			value: '994',
			id: '15',
			key: 'key15'
		},
		{
			label: '+(1242)',
			value: '1242',
			id: '16',
			key: 'key16'
		},
		{
			label: '+(973)',
			value: '973',
			id: '17',
			key: 'key17'
		},
		{
			label: '+(880)',
			value: '880',
			id: '18',
			key: 'key18'
		},
		{
			label: '+(1246)',
			value: '1246',
			id: '19',
			key: 'key19'
		},
		{
			label: '+(375)',
			value: '375',
			id: '20',
			key: 'key20'
		},
		{
			label: '+(32)',
			value: '32',
			id: '21',
			key: 'key21'
		},
		{
			label: '+(501)',
			value: '501',
			id: '22',
			key: 'key22'
		},
		{
			label: '+(229)',
			value: '229',
			id: '23',
			key: 'key23'
		},
		{
			label: '+(1441)',
			value: '1441',
			id: '24',
			key: 'key24'
		},
		{
			label: '+(975)',
			value: '975',
			id: '25',
			key: 'key25'
		},
		{
			label: '+(591)',
			value: '591',
			id: '26',
			key: 'key26'
		},
		{
			label: '+(387)',
			value: '387',
			id: '27',
			key: 'key27'
		},
		{
			label: '+(267)',
			value: '267',
			id: '28',
			key: 'key28'
		},
		{
			label: '+(47)',
			value: '47',
			id: '29',
			key: 'key29'
		},
		{
			label: '+(55)',
			value: '55',
			id: '30',
			key: 'key30'
		},
		{
			label: '+(246)',
			value: '246',
			id: '31',
			key: 'key31'
		},
		{
			label: '+(1284)',
			value: '1284',
			id: '32',
			key: 'key32'
		},
		{
			label: '+(673)',
			value: '673',
			id: '33',
			key: 'key33'
		},
		{
			label: '+(359)',
			value: '359',
			id: '34',
			key: 'key34'
		},
		{
			label: '+(226)',
			value: '226',
			id: '35',
			key: 'key35'
		},
		{
			label: '+(257)',
			value: '257',
			id: '36',
			key: 'key36'
		},
		{
			label: '+(855)',
			value: '855',
			id: '37',
			key: 'key37'
		},
		{
			label: '+(237)',
			value: '237',
			id: '38',
			key: 'key38'
		},
		{
			label: '+(1)',
			value: '1',
			id: '39',
			key: 'key39'
		},
		{
			label: '+(238)',
			value: '238',
			id: '40',
			key: 'key40'
		},
		{
			label: '+(599)',
			value: '599',
			id: '41',
			key: 'key41'
		},
		{
			label: '+(1345)',
			value: '1345',
			id: '42',
			key: 'key42'
		},
		{
			label: '+(236)',
			value: '236',
			id: '43',
			key: 'key43'
		},
		{
			label: '+(235)',
			value: '235',
			id: '44',
			key: 'key44'
		},
		{
			label: '+(56)',
			value: '56',
			id: '45',
			key: 'key45'
		},
		{
			label: '+(86)',
			value: '86',
			id: '46',
			key: 'key46'
		},
		{
			label: '+(57)',
			value: '57',
			id: '49',
			key: 'key49'
		},
		{
			label: '+(269)',
			value: '269',
			id: '50',
			key: 'key50'
		},
		{
			label: '+(243)',
			value: '243',
			id: '51',
			key: 'key51'
		},
		{
			label: '+(242)',
			value: '242',
			id: '52',
			key: 'key52'
		},
		{
			label: '+(682)',
			value: '682',
			id: '53',
			key: 'key53'
		},
		{
			label: '+(506)',
			value: '506',
			id: '54',
			key: 'key54'
		},
		{
			label: '+(225)',
			value: '225',
			id: '55',
			key: 'key55'
		},
		{
			label: '+(385)',
			value: '385',
			id: '56',
			key: 'key56'
		},
		{
			label: '+(53)',
			value: '53',
			id: '57',
			key: 'key57'
		},
		{
			label: '+(357)',
			value: '357',
			id: '59',
			key: 'key59'
		},
		{
			label: '+(420)',
			value: '420',
			id: '60',
			key: 'key60'
		},
		{
			label: '+(45)',
			value: '45',
			id: '61',
			key: 'key61'
		},
		{
			label: '+(253)',
			value: '253',
			id: '62',
			key: 'key62'
		},
		{
			label: '+(1767)',
			value: '1767',
			id: '63',
			key: 'key63'
		},
		{
			label: '+(593)',
			value: '593',
			id: '65',
			key: 'key65'
		},
		{
			label: '+(20)',
			value: '20',
			id: '66',
			key: 'key66'
		},
		{
			label: '+(503)',
			value: '503',
			id: '67',
			key: 'key67'
		},
		{
			label: '+(240)',
			value: '240',
			id: '68',
			key: 'key68'
		},
		{
			label: '+(291)',
			value: '291',
			id: '69',
			key: 'key69'
		},
		{
			label: '+(372)',
			value: '372',
			id: '70',
			key: 'key70'
		},
		{
			label: '+(251)',
			value: '251',
			id: '71',
			key: 'key71'
		},
		{
			label: '+(500)',
			value: '500',
			id: '72',
			key: 'key72'
		},
		{
			label: '+(298)',
			value: '298',
			id: '73',
			key: 'key73'
		},
		{
			label: '+(679)',
			value: '679',
			id: '74',
			key: 'key74'
		},
		{
			label: '+(33)',
			value: '33',
			id: '76',
			key: 'key76'
		},
		{
			label: '+(594)',
			value: '594',
			id: '77',
			key: 'key77'
		},
		{
			label: '+(689)',
			value: '689',
			id: '78',
			key: 'key78'
		},
		{
			label: '+(262)',
			value: '262',
			id: '79',
			key: 'key79'
		},
		{
			label: '+(241)',
			value: '241',
			id: '80',
			key: 'key80'
		},
		{
			label: '+(220)',
			value: '220',
			id: '81',
			key: 'key81'
		},
		{
			label: '+(995)',
			value: '995',
			id: '82',
			key: 'key82'
		},
		{
			label: '+(49)',
			value: '49',
			id: '83',
			key: 'key83'
		},
		{
			label: '+(233)',
			value: '233',
			id: '84',
			key: 'key84'
		},
		{
			label: '+(350)',
			value: '350',
			id: '85',
			key: 'key85'
		},
		{
			label: '+(30)',
			value: '30',
			id: '86',
			key: 'key86'
		},
		{
			label: '+(299)',
			value: '299',
			id: '87',
			key: 'key87'
		},
		{
			label: '+(1473)',
			value: '1473',
			id: '88',
			key: 'key88'
		},
		{
			label: '+(590)',
			value: '590',
			id: '89',
			key: 'key89'
		},
		{
			label: '+(1671)',
			value: '1671',
			id: '90',
			key: 'key90'
		},
		{
			label: '+(502)',
			value: '502',
			id: '91',
			key: 'key91'
		},
		{
			label: '+(44)',
			value: '44',
			id: '92',
			key: 'key92'
		},
		{
			label: '+(224)',
			value: '224',
			id: '93',
			key: 'key93'
		},
		{
			label: '+(245)',
			value: '245',
			id: '94',
			key: 'key94'
		},
		{
			label: '+(592)',
			value: '592',
			id: '95',
			key: 'key95'
		},
		{
			label: '+(509)',
			value: '509',
			id: '96',
			key: 'key96'
		},
		{
			label: '+(504)',
			value: '504',
			id: '98',
			key: 'key98'
		},
		{
			label: '+(852)',
			value: '852',
			id: '99',
			key: 'key99'
		},
		{
			label: '+(36)',
			value: '36',
			id: '100',
			key: 'key100'
		},
		{
			label: '+(354)',
			value: '354',
			id: '101',
			key: 'key101'
		},
		{
			label: '+(91)',
			value: '91',
			id: '102',
			key: 'key102'
		},
		{
			label: '+(62)',
			value: '62',
			id: '103',
			key: 'key103'
		},
		{
			label: '+(98)',
			value: '98',
			id: '104',
			key: 'key104'
		},
		{
			label: '+(964)',
			value: '964',
			id: '105',
			key: 'key105'
		},
		{
			label: '+(353)',
			value: '353',
			id: '106',
			key: 'key106'
		},
		{
			label: '+(972)',
			value: '972',
			id: '108',
			key: 'key108'
		},
		{
			label: '+(39)',
			value: '39',
			id: '109',
			key: 'key109'
		},
		{
			label: '+(1876)',
			value: '1876',
			id: '110',
			key: 'key110'
		},
		{
			label: '+(81)',
			value: '81',
			id: '111',
			key: 'key111'
		},
		{
			label: '+(962)',
			value: '962',
			id: '113',
			key: 'key113'
		},
		{
			label: '+(7)',
			value: '7',
			id: '114',
			key: 'key114'
		},
		{
			label: '+(254)',
			value: '254',
			id: '115',
			key: 'key115'
		},
		{
			label: '+(686)',
			value: '686',
			id: '116',
			key: 'key116'
		},
		{
			label: '+(383)',
			value: '383',
			id: '117',
			key: 'key117'
		},
		{
			label: '+(965)',
			value: '965',
			id: '118',
			key: 'key118'
		},
		{
			label: '+(996)',
			value: '996',
			id: '119',
			key: 'key119'
		},
		{
			label: '+(856)',
			value: '856',
			id: '120',
			key: 'key120'
		},
		{
			label: '+(371)',
			value: '371',
			id: '121',
			key: 'key121'
		},
		{
			label: '+(961)',
			value: '961',
			id: '122',
			key: 'key122'
		},
		{
			label: '+(266)',
			value: '266',
			id: '123',
			key: 'key123'
		},
		{
			label: '+(231)',
			value: '231',
			id: '124',
			key: 'key124'
		},
		{
			label: '+(218)',
			value: '218',
			id: '125',
			key: 'key125'
		},
		{
			label: '+(423)',
			value: '423',
			id: '126',
			key: 'key126'
		},
		{
			label: '+(370)',
			value: '370',
			id: '127',
			key: 'key127'
		},
		{
			label: '+(352)',
			value: '352',
			id: '128',
			key: 'key128'
		},
		{
			label: '+(853)',
			value: '853',
			id: '129',
			key: 'key129'
		},
		{
			label: '+(389)',
			value: '389',
			id: '130',
			key: 'key130'
		},
		{
			label: '+(261)',
			value: '261',
			id: '131',
			key: 'key131'
		},
		{
			label: '+(265)',
			value: '265',
			id: '132',
			key: 'key132'
		},
		{
			label: '+(60)',
			value: '60',
			id: '133',
			key: 'key133'
		},
		{
			label: '+(960)',
			value: '960',
			id: '134',
			key: 'key134'
		},
		{
			label: '+(223)',
			value: '223',
			id: '135',
			key: 'key135'
		},
		{
			label: '+(356)',
			value: '356',
			id: '136',
			key: 'key136'
		},
		{
			label: '+(692)',
			value: '692',
			id: '137',
			key: 'key137'
		},
		{
			label: '+(596)',
			value: '596',
			id: '138',
			key: 'key138'
		},
		{
			label: '+(222)',
			value: '222',
			id: '139',
			key: 'key139'
		},
		{
			label: '+(230)',
			value: '230',
			id: '140',
			key: 'key140'
		},
		{
			label: '+(52)',
			value: '52',
			id: '142',
			key: 'key142'
		},
		{
			label: '+(691)',
			value: '691',
			id: '143',
			key: 'key143'
		},
		{
			label: '+(373)',
			value: '373',
			id: '144',
			key: 'key144'
		},
		{
			label: '+(377)',
			value: '377',
			id: '145',
			key: 'key145'
		},
		{
			label: '+(976)',
			value: '976',
			id: '146',
			key: 'key146'
		},
		{
			label: '+(382)',
			value: '382',
			id: '147',
			key: 'key147'
		},
		{
			label: '+(1664)',
			value: '1664',
			id: '148',
			key: 'key148'
		},
		{
			label: '+(212)',
			value: '212',
			id: '149',
			key: 'key149'
		},
		{
			label: '+(258)',
			value: '258',
			id: '150',
			key: 'key150'
		},
		{
			label: '+(95)',
			value: '95',
			id: '151',
			key: 'key151'
		},
		{
			label: '+(264)',
			value: '264',
			id: '152',
			key: 'key152'
		},
		{
			label: '+(674)',
			value: '674',
			id: '153',
			key: 'key153'
		},
		{
			label: '+(977)',
			value: '977',
			id: '154',
			key: 'key154'
		},
		{
			label: '+(31)',
			value: '31',
			id: '155',
			key: 'key155'
		},
		{
			label: '+(687)',
			value: '687',
			id: '156',
			key: 'key156'
		},
		{
			label: '+(64)',
			value: '64',
			id: '157',
			key: 'key157'
		},
		{
			label: '+(505)',
			value: '505',
			id: '158',
			key: 'key158'
		},
		{
			label: '+(227)',
			value: '227',
			id: '159',
			key: 'key159'
		},
		{
			label: '+(234)',
			value: '234',
			id: '160',
			key: 'key160'
		},
		{
			label: '+(683)',
			value: '683',
			id: '161',
			key: 'key161'
		},
		{
			label: '+(850)',
			value: '850',
			id: '163',
			key: 'key163'
		},
		{
			label: '+(1670)',
			value: '1670',
			id: '164',
			key: 'key164'
		},
		{
			label: '+(968)',
			value: '968',
			id: '166',
			key: 'key166'
		},
		{
			label: '+(92)',
			value: '92',
			id: '167',
			key: 'key167'
		},
		{
			label: '+(680)',
			value: '680',
			id: '168',
			key: 'key168'
		},
		{
			label: '+(970)',
			value: '970',
			id: '169',
			key: 'key169'
		},
		{
			label: '+(507)',
			value: '507',
			id: '170',
			key: 'key170'
		},
		{
			label: '+(675)',
			value: '675',
			id: '171',
			key: 'key171'
		},
		{
			label: '+(595)',
			value: '595',
			id: '172',
			key: 'key172'
		},
		{
			label: '+(51)',
			value: '51',
			id: '173',
			key: 'key173'
		},
		{
			label: '+(63)',
			value: '63',
			id: '174',
			key: 'key174'
		},
		{
			label: '+(48)',
			value: '48',
			id: '176',
			key: 'key176'
		},
		{
			label: '+(351)',
			value: '351',
			id: '177',
			key: 'key177'
		},
		{
			label: '+(974)',
			value: '974',
			id: '179',
			key: 'key179'
		},
		{
			label: '+(40)',
			value: '40',
			id: '181',
			key: 'key181'
		},
		{
			label: '+(250)',
			value: '250',
			id: '183',
			key: 'key183'
		},
		{
			label: '+(290)',
			value: '290',
			id: '185',
			key: 'key185'
		},
		{
			label: '+(1869)',
			value: '1869',
			id: '186',
			key: 'key186'
		},
		{
			label: '+(1758)',
			value: '1758',
			id: '187',
			key: 'key187'
		},
		{
			label: '+(508)',
			value: '508',
			id: '189',
			key: 'key189'
		},
		{
			label: '+(1784)',
			value: '1784',
			id: '190',
			key: 'key190'
		},
		{
			label: '+(685)',
			value: '685',
			id: '191',
			key: 'key191'
		},
		{
			label: '+(378)',
			value: '378',
			id: '192',
			key: 'key192'
		},
		{
			label: '+(239)',
			value: '239',
			id: '193',
			key: 'key193'
		},
		{
			label: '+(966)',
			value: '966',
			id: '194',
			key: 'key194'
		},
		{
			label: '+(221)',
			value: '221',
			id: '195',
			key: 'key195'
		},
		{
			label: '+(381)',
			value: '381',
			id: '196',
			key: 'key196'
		},
		{
			label: '+(248)',
			value: '248',
			id: '197',
			key: 'key197'
		},
		{
			label: '+(232)',
			value: '232',
			id: '198',
			key: 'key198'
		},
		{
			label: '+(65)',
			value: '65',
			id: '199',
			key: 'key199'
		},
		{
			label: '+(1721)',
			value: '1721',
			id: '200',
			key: 'key200'
		},
		{
			label: '+(421)',
			value: '421',
			id: '201',
			key: 'key201'
		},
		{
			label: '+(386)',
			value: '386',
			id: '202',
			key: 'key202'
		},
		{
			label: '+(677)',
			value: '677',
			id: '203',
			key: 'key203'
		},
		{
			label: '+(252)',
			value: '252',
			id: '204',
			key: 'key204'
		},
		{
			label: '+(27)',
			value: '27',
			id: '205',
			key: 'key205'
		},
		{
			label: '+(82)',
			value: '82',
			id: '207',
			key: 'key207'
		},
		{
			label: '+(211)',
			value: '211',
			id: '208',
			key: 'key208'
		},
		{
			label: '+(34)',
			value: '34',
			id: '209',
			key: 'key209'
		},
		{
			label: '+(94)',
			value: '94',
			id: '210',
			key: 'key210'
		},
		{
			label: '+(249)',
			value: '249',
			id: '211',
			key: 'key211'
		},
		{
			label: '+(597)',
			value: '597',
			id: '212',
			key: 'key212'
		},
		{
			label: '+(268)',
			value: '268',
			id: '214',
			key: 'key214'
		},
		{
			label: '+(46)',
			value: '46',
			id: '215',
			key: 'key215'
		},
		{
			label: '+(41)',
			value: '41',
			id: '216',
			key: 'key216'
		},
		{
			label: '+(963)',
			value: '963',
			id: '217',
			key: 'key217'
		},
		{
			label: '+(886)',
			value: '886',
			id: '218',
			key: 'key218'
		},
		{
			label: '+(992)',
			value: '992',
			id: '219',
			key: 'key219'
		},
		{
			label: '+(255)',
			value: '255',
			id: '220',
			key: 'key220'
		},
		{
			label: '+(66)',
			value: '66',
			id: '221',
			key: 'key221'
		},
		{
			label: '+(670)',
			value: '670',
			id: '222',
			key: 'key222'
		},
		{
			label: '+(228)',
			value: '228',
			id: '223',
			key: 'key223'
		},
		{
			label: '+(690)',
			value: '690',
			id: '224',
			key: 'key224'
		},
		{
			label: '+(676)',
			value: '676',
			id: '225',
			key: 'key225'
		},
		{
			label: '+(1868)',
			value: '1868',
			id: '226',
			key: 'key226'
		},
		{
			label: '+(216)',
			value: '216',
			id: '227',
			key: 'key227'
		},
		{
			label: '+(90)',
			value: '90',
			id: '228',
			key: 'key228'
		},
		{
			label: '+(993)',
			value: '993',
			id: '229',
			key: 'key229'
		},
		{
			label: '+(1649)',
			value: '1649',
			id: '230',
			key: 'key230'
		},
		{
			label: '+(688)',
			value: '688',
			id: '231',
			key: 'key231'
		},
		{
			label: '+(1340)',
			value: '1340',
			id: '232',
			key: 'key232'
		},
		{
			label: '+(256)',
			value: '256',
			id: '233',
			key: 'key233'
		},
		{
			label: '+(380)',
			value: '380',
			id: '234',
			key: 'key234'
		},
		{
			label: '+(971)',
			value: '971',
			id: '235',
			key: 'key235'
		},
		{
			label: '+(598)',
			value: '598',
			id: '239',
			key: 'key239'
		},
		{
			label: '+(998)',
			value: '998',
			id: '240',
			key: 'key240'
		},
		{
			label: '+(678)',
			value: '678',
			id: '241',
			key: 'key241'
		},
		{
			label: '+(58)',
			value: '58',
			id: '243',
			key: 'key243'
		},
		{
			label: '+(84)',
			value: '84',
			id: '244',
			key: 'key244'
		},
		{
			label: '+(681)',
			value: '681',
			id: '245',
			key: 'key245'
		},
		{
			label: '+(967)',
			value: '967',
			id: '247',
			key: 'key247'
		},
		{
			label: '+(260)',
			value: '260',
			id: '248',
			key: 'key248'
		},
		{
			label: '+(263)',
			value: '263',
			id: '249',
			key: 'key249'
		}
	];
	constructor() {
		this.filterProvinceByCode(this.currentCountryCode);

		makeAutoObservable(this, {
			filterProvinceByCode: action.bound,
			countriesOptions: computed
		});
	}
	public get countriesOptions(): IOptions[] {
		return this.countries.map((c) => {
			return {
				id: c.id,
				key: c.isCode3,
				value: c.isoCode,
				label: c.name
			};
		});
	}
	public filterProvinceByCode(code: string): void {
		this.currentCountryCode = code;
		this.provincesByCountry = this.provinces.filter((c) => c.countryCode == code);
	}

	public getCountryNameByCode(code?: string): string | undefined {
		return this.listOfCountry.find((c) => c.value == code)?.label;
	}

	public getNationalityNameByCode(code?: string): string | undefined {
		return this.nationalities.find((c) => c.value == code)?.label;
	}

	public getPersonTitleText(code: string | undefined): string {
		if (!code) return '';
		const titleOption = this.titleOptions.find(op => op.value === code);
		return titleOption ? titleOption.label : '';
	}

	public filterGenderByTitle(isChineseNationality: boolean, title: string) {
		const listGender = isChineseNationality ? this.genderOptions : this.genderAndXOptions;
		if (!title || title === TITLE.OTHERS) {
			return listGender;
		}
		if (title === TITLE.MR) {
			return listGender.filter((item: IOptions) => item.label !== GENDER_OTHERS.FEMALE);
		}
		return listGender.filter((item: IOptions) => item.label !== GENDER_OTHERS.MALE);
	}
}
